import React, { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { KTCard, KTCardBody, KTSVG } from '../../../../_metronic/helpers'
import { getDocs, deleteDoc, updateDoc, toggleDocPublish, toggleDocFeatured } from './core/_requests'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import { DeleteModal } from '../../../../_component/table/DeleteModal'
import { Plus, Pencil, Eye, EyeOff, Trash2, Search, FileText, MessageSquare, Star } from 'lucide-react'
import { Skeleton } from '../../../../_component/Skeleton'

const DocsList = () => {
  const queryClient = useQueryClient()
  const [deleteId, setDeleteId] = useState<string | number | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const { data: docs, isLoading } = useQuery(['docs'], getDocs)

  const deleteMutation = useMutation(deleteDoc, {
    onSuccess: () => {
      queryClient.invalidateQueries(['docs'])
      setDeleteId(null)
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to delete document'
      toast.error(message)
    },
  })

  const toggleMutation = useMutation(
    ({ id, status }: { id: string | number; status: 'PUBLISHED' | 'DRAFT' }) =>
      toggleDocPublish(id, status),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['docs'])
      },
      onError: (error: any) => {
        const message = error.response?.data?.message || 'Failed to update document status'
        toast.error(message)
      },
    }
  )

  const featuredMutation = useMutation(
    ({ id, featured }: { id: string | number; featured: boolean }) =>
      toggleDocFeatured(id, featured),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['docs'])
      },
      onError: (error: any) => {
        const message = error.response?.data?.message || 'Failed to update featured status'
        toast.error(message)
      },
    }
  )

  const filteredDocs = useMemo(() => {
    if (!Array.isArray(docs)) return []
    if (!searchTerm.trim()) return docs
    const q = searchTerm.toLowerCase()
    return docs.filter(
      (doc) =>
        doc.title.toLowerCase().includes(q) ||
        (doc.categories || []).some((cat) => cat.name.toLowerCase().includes(q))
    )
  }, [docs, searchTerm])

  if (isLoading) {
    return (
      <div className='row g-5 g-xl-8'>
        <div className='col-xl-12'>
          <div className='bg-white border border-gray-200 rounded-lg shadow-sm'>
            <div className='card-header border-0 pt-5'>
              <h3 className='card-title align-items-start flex-column'>
                <Skeleton width={150} height={24} />
              </h3>
            </div>
            <div className='p-4'>
              <div className='overflow-x-auto'>
                <table className='min-w-full table-auto border-separate border-spacing-y-1 text-sm'>
                  <thead>
                    <tr className='text-left text-white font-semibold text-xs uppercase tracking-wider bg-[#008080]'>
                      <th className='px-4 py-3.5 rounded-tl-lg'>Title</th>
                      <th className='px-4 py-3.5'>Status</th>
                      <th className='px-4 py-3.5'>Categories</th>
                      <th className='px-4 py-3.5'>Created At</th>
                      <th className='px-4 py-3.5 rounded-tr-lg text-right'>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <tr key={i} className='even:bg-[#e6f2f2] odd:bg-white'>
                        <td className='px-4 py-3.5'><Skeleton width='60%' height={16} /></td>
                        <td className='px-4 py-3.5'><Skeleton width={80} height={20} /></td>
                        <td className='px-4 py-3.5'><Skeleton width='40%' height={14} /></td>
                        <td className='px-4 py-3.5'><Skeleton width={100} height={14} /></td>
                        <td className='px-4 py-3.5 text-right'><Skeleton width={100} height={28} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className='bg-white rounded-lg border border-gray-200 px-6 py-4 my-5 shadow-sm'>
        <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-4'>
          <div>
            <h3 className='text-lg font-bold text-gray-800 uppercase tracking-tight'>Documentation</h3>
            <p className='text-xs text-gray-500 font-medium mt-0.5'>
              {docs?.length || 0} document{(docs?.length || 0) !== 1 ? 's' : ''} total
            </p>
          </div>
          <div className='flex items-center gap-3'>
            <div className='relative'>
              <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400' />
              <input
                type='text'
                placeholder='Search docs...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='w-full sm:w-64 pl-9 pr-4 py-2 text-sm bg-gray-100 border border-transparent rounded-lg focus:outline-none focus:ring-2 focus:ring-[#008080]/30 focus:bg-white focus:border-[#008080] transition-all placeholder:text-gray-400'
              />
            </div>
            <Link
              to='categories'
              className='inline-flex items-center gap-2 px-4 py-2 bg-[#ccf2f2] text-[#008080] text-sm font-medium rounded-lg hover:bg-[#b3e6e6] transition-colors duration-200'
            >
              <KTSVG path='/media/icons/duotune/general/gen010.svg' className='w-4 h-4' />
              Manage Categories
            </Link>

            <Link
              to='new'
              className='inline-flex items-center gap-2 px-4 py-2 bg-[#008080] text-white text-sm font-medium rounded-lg hover:bg-[#007373] transition-colors duration-200 shadow-sm'
            >
              <Plus className='w-5 h-5' />
              New Doc
            </Link>
          </div>
        </div>
      </div>

      <KTCard className='border-gray-200 shadow-sm'>
        <KTCardBody className='py-3'>
          <div className='overflow-x-auto'>
            <table className='min-w-full table-auto border-separate border-spacing-y-0.5 text-sm'>
              <thead>
                <tr className='text-left text-white font-semibold text-xs uppercase tracking-wider bg-[#008080]'>
                  <th className='px-4 py-3.5 rounded-tl-lg'>Title</th>
                  <th className='px-4 py-3.5'>Status</th>
                  <th className='px-4 py-3.5'>Categories</th>
                  <th className='px-4 py-3.5'>Created On</th>
                  <th className='px-4 py-3.5 rounded-tr-lg text-right'>Actions</th>
                </tr>
              </thead>
              <tbody className='text-gray-700 font-semibold'>
                {filteredDocs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className='text-center text-gray-400 py-12'>
                      <div className='flex flex-col items-center gap-2'>
                        <FileText className='w-8 h-8 opacity-20' />
                        {searchTerm ? 'No documents match your search.' : 'No documents found.'}
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredDocs.map((doc) => (
                    <tr
                      key={doc.id}
                      className='even:bg-[#e6f2f2] odd:bg-white hover:bg-[#ccf2f2]/50 transition-colors duration-150'
                    >
                      <td className='px-4 py-3.5'>
                        <div className='flex items-center gap-1.5'>
                          {doc.featured && (
                            <Star className='w-3.5 h-3.5 text-[#008080] fill-[#008080] flex-shrink-0' />
                          )}
                          <Link
                            to={`${doc.id}`}
                            className='text-gray-800 font-bold hover:text-[#008080] transition-colors duration-200'
                          >
                            {doc.title}
                          </Link>
                        </div>
                      </td>
                      <td className='px-4 py-3.5'>
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${doc.status === 'PUBLISHED'
                            ? 'bg-[#008080] text-white'
                            : 'bg-gray-200 text-gray-700'
                            }`}
                        >
                          <span
                            className={`w-1 h-1 rounded-full ${doc.status === 'PUBLISHED' ? 'bg-white' : 'bg-gray-500'
                              }`}
                          ></span>
                          {doc.status === 'PUBLISHED' ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td className='px-4 py-3.5'>
                        <div className='flex flex-wrap gap-1'>
                          {(doc.categories || []).map((cat) => (
                            <span
                              key={cat.id}
                              className='px-2 py-0.5 rounded-md text-[10px] bg-[#008080]/10 text-[#008080]'
                            >
                              {cat.name}
                            </span>
                          ))}
                          {(doc.categories || []).length === 0 && (
                            <span className='text-gray-300 text-[10px] italic'>Uncategorized</span>
                          )}
                        </div>
                      </td>
                      <td className='px-4 py-3.5'>
                        <span className='text-gray-500 text-xs'>
                          {dayjs(doc.created_at).format('MMM DD, YYYY')}
                        </span>
                      </td>
                      <td className='px-4 py-3.5 text-right'>
                        <div className='flex items-center justify-end gap-1'>
                          <button
                            className={`inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors duration-200 ${
                              doc.featured
                                ? 'bg-[#ccf2f2] text-[#008080] hover:bg-[#b3e6e6]'
                                : 'bg-[#ccf2f2] text-gray-400 hover:bg-[#b3e6e6]'
                            }`}
                            onClick={() => featuredMutation.mutate({
                              id: doc.id as string | number,
                              featured: !doc.featured,
                            })}
                            title={doc.featured ? 'Unfeature' : 'Feature'}
                          >
                            <Star className={`w-4 h-4 ${doc.featured ? 'fill-[#008080]' : ''}`} />
                          </button>
                          <Link
                            to={`${doc.id}/edit`}
                            className='inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#ccf2f2] text-[#008080] hover:bg-[#b3e6e6] transition-colors duration-200'
                            title='Edit'
                          >
                            <Pencil className='w-4 h-4' />
                          </Link>
                          <button
                            className='inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#ccf2f2] text-[#008080] hover:bg-[#b3e6e6] transition-colors duration-200'
                            onClick={() => toggleMutation.mutate({
                              id: doc.id as string | number,
                              status: doc.status as 'PUBLISHED' | 'DRAFT',
                            })}
                            title={doc.status === 'PUBLISHED' ? 'Draft' : 'Publish'}
                          >
                            {doc.status === 'PUBLISHED' ? (
                              <EyeOff className='w-4 h-4' />
                            ) : (
                              <Eye className='w-4 h-4' />
                            )}
                          </button>
                          <button
                            className='inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#ccf2f2] text-[#008080] hover:bg-[#b3e6e6] transition-colors duration-200'
                            onClick={() => setDeleteId(doc.id as string | number)}
                            title='Delete'
                          >
                            <Trash2 className='w-4 h-4' />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </KTCardBody>
      </KTCard>

      <DeleteModal
        showDeleteConfirm={deleteId !== null}
        setShowDeleteConfirm={(show) => { if (!show) setDeleteId(null) }}
        deleteAction={(id) => deleteMutation.mutateAsync(id)}
        refetch={() => queryClient.invalidateQueries(['docs'])}
        id={deleteId}
      />
    </>
  )
}

export default DocsList